import type { ServerConnection } from "@/context/server"
import type { StarterMessage } from "@/context/session/starters"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"
import { serverFetch } from "@/transport/server"

function decodeStarterMessage(value: unknown): StarterMessage {
  const item = jsonObject(value, "Server returned an invalid starter message")
  jsonString(item.sessionID, "Server returned an invalid starter message")
  jsonString(item.messageID, "Server returned an invalid starter message")
  jsonString(item.workspaceID, "Server returned an invalid starter message")
  jsonString(item.sessionTitle, "Server returned an invalid starter message")
  if (item.role !== "user" && item.role !== "assistant") throw new Error("Server returned an invalid starter message")
  jsonString(item.preview, "Server returned an invalid starter message")
  jsonNumber(item.timeCreated, "Server returned an invalid starter message")
  for (const key of ["providerID", "modelID", "variant"] as const) {
    if (item[key] !== undefined) jsonString(item[key], "Server returned an invalid starter message")
  }
  return item as StarterMessage
}

function decodeStarterMessages(value: unknown) {
  return jsonArray(value, "Server returned invalid starter messages").map(decodeStarterMessage)
}

export async function listStarterMessagesEndpoint(server: ServerConnection.HttpBase, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, "/starter", { signal }),
    decodeStarterMessages,
    { error: "Failed to load starter messages" },
  )
}

export async function saveStarterMessage(server: ServerConnection.HttpBase, item: StarterMessage, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, "/starter", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
      signal,
    }),
    decodeStarterMessage,
    { error: "Failed to save starter message" },
  )
}

export async function updateStarterMessageModel(
  server: ServerConnection.HttpBase,
  input: { sessionID: string; messageID: string; providerID: string; modelID: string; variant: string },
  signal?: AbortSignal,
) {
  await expectResponse(
    await serverFetch(
      server,
      `/session/${encodeURIComponent(input.sessionID)}/starter/${encodeURIComponent(input.messageID)}/model`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ providerID: input.providerID, modelID: input.modelID, variant: input.variant }),
        signal,
      },
    ),
    { error: "Failed to update starter model" },
  )
}

export async function deleteStarterMessage(
  server: ServerConnection.HttpBase,
  sessionID: string,
  messageID: string,
  signal?: AbortSignal,
) {
  await expectResponse(
    await serverFetch(
      server,
      `/session/${encodeURIComponent(sessionID)}/starter/${encodeURIComponent(messageID)}`,
      { method: "DELETE", signal },
    ),
    { allow: [404], error: "Failed to remove starter message" },
  )
}
